import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
} from 'src/app/core/dx-types';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	deleteExportTemplatesById,
	ExportTemplateSelector,
	initExportTemplateList,
	navigateToExportTemplateCreate,
	navigateToExportTemplateEdit,
	refreshExportTemplateList,
	toggleExportStatus,
	updateIncludePublic,
} from '../../store';
import { ExportTemplateListItemEntity } from './export-template-list-item.entity';
import { ExportTemplateListEntity } from './export-template-list.entity';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};
@Component({
	selector: 'app-export-template-list',
	templateUrl: './export-template-list.component.html',
	styleUrls: ['./export-template-list.component.scss'],
})
export class ExportTemplateListComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const exportTemplate = item as ExportTemplateListItemEntity;
				this.toggleActivationStatus(exportTemplate);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const exportTemplate = item as ExportTemplateListItemEntity;
				this.toggleActivationStatus(exportTemplate);
			},
			icon: ActionIcons.ActivateElement,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: ExportTemplateListEntity;
		includePublic: boolean;
	}>;
	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly exportTemplateListSubject =
		new ReplaySubject<ExportTemplateListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly exportTemplateSelector: ExportTemplateSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'export-template-list';
		this.dxLocalStorageKey = 'dx.grid.exportTemplate-list';

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.exportTemplateSelector.getLoading$(),
			list: this.exportTemplateListSubject,
			includePublic: this.exportTemplateSelector.includePublic$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initExportTemplateList());
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.exportTemplateSelector
			.getExportTemplateList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators()
						.map((t) => ({
							text: t,
							value: t,
						}))
						.toArray();

					this.exportTemplateListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshExportTemplateList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewExportTemplate(): void {
		this.store.dispatch(navigateToExportTemplateCreate());
	}

	public navigateToExportTemplate(
		exportTemplate: ExportTemplateListItemEntity
	): void {
		this.store.dispatch(
			navigateToExportTemplateEdit({
				exportConfigId: exportTemplate.id,
			})
		);
	}

	public async deleteExportTemplate(
		event: Event,
		exportTemplate: ExportTemplateListItemEntity
	): Promise<void> {
		event.stopPropagation();
		const includePublic = await firstValueFrom(
			this.exportTemplateSelector.includePublic$()
		);
		this.store.dispatch(
			deleteExportTemplatesById({
				elemIds: [exportTemplate.id],
				excludePublic: !includePublic,
				excludeInactive: false,
			})
		);
	}

	public toggleIncludePublic(value: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: value.value,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ExportTemplateListItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ExportTemplateListItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedTemplates = await firstValueFrom(
			this.exportTemplateSelector.getSelectedExportTemplates$(selectedRowKeys)
		);
		const includePublic = await firstValueFrom(
			this.exportTemplateSelector.includePublic$()
		);
		this.store.dispatch(
			deleteExportTemplatesById({
				elemIds: selectedTemplates.map((e) => e.id),
				excludePublic: !includePublic,
				excludeInactive: false,
			})
		);
	}

	private toggleActivationStatus(
		exportTemplate: ExportTemplateListItemEntity
	): void {
		this.store.dispatch(
			toggleExportStatus({
				exportId: exportTemplate.id,
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@export.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@export.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
